import { useNavigate } from "react-router";
import { BookIcon, DocumentTextIcon, Help_1Icon, TableIcon } from "vega-icons";
import { NofilesIllustration } from "vega-illustrations";

import { FaqPlacement } from "@vega-api/generated/types/faqPlacement";
import { CopyButton } from "@vega-core/components/Buttons/CopyButton/CopyButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ContactCard } from "@vega-core/components/Cards/ContactCard/ContactCard";
import { FAQList } from "@vega-core/components/FAQ/FAQList";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { type FAQItemType } from "@vega-core/models/faq.type";
import { m } from "@vega-core/paraglide/messages";

import { useResourcesRoute } from "../../../../../hooks/useResourcesRoute";
import { buildFaqItemBodyHtml } from "../../../../HelpCentreCMS/utils/buildFaqItemBodyHtml";
import { filterEmptyFaqItems } from "../../../../HelpCentreCMS/utils/filterEmptyFaqItems";
import { useCmsFaqs } from "../../../../ResourceVegaCMS/hooks/useCmsFaqs";
import { ResourceCard } from "../../ResourceCard";
import { ModalRoutes } from "../constants";
import { useFaqItems } from "../usefaqItems";

const mapCmsFaqsToItems = (
  faqCategories: ReturnType<typeof useCmsFaqs>["faqCategories"],
): FAQItemType[] =>
  (faqCategories ?? []).flatMap((category) =>
    filterEmptyFaqItems(category.items).map((item, index) => ({
      id: `cms-faq-${category.id}-${index}`,
      title: item.question,
      body: buildFaqItemBodyHtml(item),
    })),
  );

const SectionWithIcon = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text/primary border">
        <span className="h-4 w-4 shrink-0">{icon}</span>
      </div>
      <h4 className="body-semi-large-semibold text-text/secondary">{title}</h4>
    </div>
    {children}
  </div>
);

export const HomePage = ({
  navigate,
}: {
  navigate: (route: string) => void;
}) => {
  const navigateGlobal = useNavigate();
  const resourcesRoute = useResourcesRoute();
  const {
    contacts: { helpEmail },
  } = usePublicConfig();
  const fallbackFaqItems = useFaqItems(navigate);
  const {
    isFlagEnabled,
    faqCategories,
    isLoading: isLoadingFaqs,
  } = useCmsFaqs(FaqPlacement.SETTINGS_HELP_PAGE);

  const faqItems: FAQItemType[] = isFlagEnabled
    ? mapCmsFaqsToItems(faqCategories)
    : fallbackFaqItems;

  return (
    <div className="flex flex-1 flex-col gap-8 overflow-auto px-6 pb-4">
      <SectionWithIcon
        icon={<Help_1Icon className="h-4 w-4" />}
        title={m.titles_generalInquires()}
      >
        <div className="flex flex-col gap-2">
          <p className="body-base-regular text-text/secondary">
            {m.settings_supportContactDescription()}
          </p>
          <div className="flex items-center gap-2">
            <span className="body-base-regular text-text/interactive">
              {helpEmail}
            </span>
            <CopyButton className="text-text/secondary" text={helpEmail} />
          </div>
        </div>
      </SectionWithIcon>

      <SectionWithIcon
        icon={<BookIcon className="h-4 w-4" />}
        title={m.navTitles_resources()}
      >
        <div className="flex flex-col gap-3">
          <ResourceCard
            description={m.settings_resourcesRolesDescription()}
            icon={<DocumentTextIcon className="h-5 w-5" />}
            onClick={() => navigate(ModalRoutes.Roles)}
            title={m.settings_resourcesRolesTitle()}
          />
          <ResourceCard
            description={m.settings_resourcesPermissionsDescription()}
            icon={<TableIcon className="h-5 w-5" />}
            onClick={() => navigate(ModalRoutes.Permissions)}
            title={m.settings_resourcesPermissionsTitle()}
          />
          <ThemedButton
            dataTestId="view-more-resources"
            onClick={() => navigateGlobal(resourcesRoute)}
            variant="secondary"
          >
            {m.settings_viewMoreResources()}
          </ThemedButton>
        </div>
      </SectionWithIcon>

      <SectionWithIcon
        icon={<Help_1Icon className="h-4 w-4" />}
        title={m.navTitles_faq()}
      >
        <FAQList
          data={faqItems}
          emptyState={{
            illustration: <NofilesIllustration />,
            title: m.localcms_resources_faq_empty(),
          }}
          isLoading={isFlagEnabled ? isLoadingFaqs : undefined}
          withBorders
        />
      </SectionWithIcon>

      <ContactCard
        align="left"
        className="bg-background/secondary shadow-none"
        data-analytics="gtm-resource-apollo-investor-portal"
        faqButtonText={m.help_viewResources()}
        onFaqClick={() => navigateGlobal(resourcesRoute)}
      />
    </div>
  );
};
